import { useMemo, useState } from "react";

import { LABEL_COLORS } from "features/labels/constants";
import useAllLabels from "features/labels/datahooks/useAllLabels";
import type { Label } from "features/labels/types";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import {
  dropdownMenuItemVariants,
  dropdownMenuListVariants,
} from "common/components/ui/DropdownMenu";
import useLeadLabelsMutations from "common/datahooks/useLeadLabelsMutations";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Lead } from "common/types";

import { startLeadsBulkAction } from "../../../utils";

import type { CheckedState } from "@radix-ui/react-checkbox";

export interface UpdateLabelsProps {
  selectedLeads: Lead[];
  onBack?: () => void;
  onClose: () => void;
}

export default function UpdateLabels({
  selectedLeads,
  onBack,
  onClose,
}: UpdateLabelsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [searchText, setSearchText] = useState("");
  const [pendingChanges, setPendingChanges] = useState<
    Record<string, Record<string, "add" | "remove">>
  >({});
  const { allLabels } = useAllLabels("workspace");
  const { addLeadLabel, removeLeadLabel } = useLeadLabelsMutations();

  function onCheckedChange(labelId: string, newCheckedState: CheckedState) {
    const leadsWithNoLabel = selectedLeads.filter(
      ({ labels: leadLabels }) => !leadLabels.find(({ id }) => id === labelId),
    );
    const leadsWithLabel = selectedLeads.filter(
      ({ labels: leadLabels }) => !!leadLabels.find(({ id }) => id === labelId),
    );

    setPendingChanges((prev) => {
      const next = { ...prev };

      if (newCheckedState === true) {
        leadsWithNoLabel.forEach(({ id }) => {
          next[id] = { ...next[id], [labelId]: "add" };
        });
        leadsWithLabel.forEach(({ id }) => {
          if (next[id]) {
            next[id] = { ...next[id] };
            delete next[id][labelId];
            if (!Object.keys(next[id]).length) delete next[id];
          }
        });
      } else {
        leadsWithLabel.forEach(({ id }) => {
          next[id] = { ...next[id], [labelId]: "remove" };
        });
        leadsWithNoLabel.forEach(({ id }) => {
          if (next[id]) {
            next[id] = { ...next[id] };
            delete next[id][labelId];
            if (!Object.keys(next[id]).length) delete next[id];
          }
        });
      }
      return next;
    });
  }

  function getEffectiveCheckedState(labelId: string): CheckedState {
    const isAllChecked = selectedLeads.every(
      ({ id, labels }) =>
        (!!labels.find((label) => label.id === labelId) ||
          pendingChanges[id]?.[labelId] === "add") &&
        pendingChanges[id]?.[labelId] !== "remove",
    );
    const isSomeChecked = selectedLeads.some(
      ({ id, labels }) =>
        !!labels.find((label) => label.id === labelId) &&
        pendingChanges[id]?.[labelId] !== "remove",
    );
    if (isAllChecked) {
      return true;
    }
    if (isSomeChecked) {
      return "indeterminate";
    }
    return false;
  }

  function onSave() {
    const leadsWithChanges = selectedLeads.filter(
      ({ id }) => !!pendingChanges[id],
    );
    const allActions = Object.values(pendingChanges).flatMap(Object.values);
    const hasAdditions = allActions.some((action) => action === "add");
    const hasRemovals = allActions.some((action) => action === "remove");

    startLeadsBulkAction(
      (lead, abortSignal) =>
        Promise.all(
          typedObjectEntries(pendingChanges[lead.id]).map(
            ([labelId, action]) => {
              const label = allLabels.find(({ id }) => id === labelId);
              if (!label) {
                return;
              }
              const actionFunction =
                action === "add" ? addLeadLabel : removeLeadLabel;
              return actionFunction({
                profileId: lead.id,
                label,
                abortSignal,
                updateCacheOn: "success",
              });
            },
          ),
        ),
      leadsWithChanges,
      (count) => {
        if (count === leadsWithChanges.length) {
          if (hasAdditions && hasRemovals) {
            return "Label changes saved";
          }
          if (hasAdditions) {
            return "Labels added to leads";
          }
          return "Labels removed from leads";
        }
        if (hasAdditions && hasRemovals) {
          return `Saved label changes for ${count}/${leadsWithChanges.length} leads`;
        }
        if (hasAdditions) {
          return `Added labels to ${count}/${leadsWithChanges.length} leads`;
        }
        return `Removed labels from ${count}/${leadsWithChanges.length} leads`;
      },
    );

    onClose();
  }

  const visibleLabels = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const filtered = allLabels.filter(({ name }) =>
      normalizedSearch ? name.toLowerCase().includes(normalizedSearch) : true,
    );

    return filtered.sort((labelA, labelB) => {
      const score = (label: Label) => {
        const checkedState = getEffectiveCheckedState(label.id);
        if (checkedState === true) return 2;
        if (checkedState === "indeterminate") return 1;
        return 0;
      };
      const scoreDiff = score(labelB) - score(labelA);
      if (scoreDiff !== 0) return scoreDiff;
      return labelA.name.localeCompare(labelB.name, undefined, {
        numeric: true,
      });
    });
  }, [allLabels, searchText]);

  const buttonComponents = (
    <>
      <Button
        size={isDesktop ? "sm" : "md"}
        className="lg:flex-1"
        variant="secondary-black"
        onClick={() => {
          console.log("close");
          setPendingChanges({});
          onClose();
        }}
      >
        Cancel
      </Button>

      <Button
        size={isDesktop ? "sm" : "md"}
        className="lg:flex-1"
        disabled={!Object.keys(pendingChanges).length}
        onClick={onSave}
      >
        Save
      </Button>
    </>
  );

  const labelsListComponent = (
    <div
      className={dropdownMenuListVariants({
        size: isDesktop ? "sm" : "md",
        isHeightLimited: true,
        class: "lg:w-60",
      })}
    >
      {visibleLabels.map((label) => {
        const checkedState = getEffectiveCheckedState(label.id);
        return (
          <button
            type="button"
            key={label.id}
            onClick={() => {
              onCheckedChange(label.id, checkedState !== true);
            }}
            className={dropdownMenuItemVariants({ size: "sm" })}
          >
            <Checkbox size="sm" isStyleOnly checked={checkedState} />
            <div
              className={`size-3.5 m-0.5 shrink-0 rounded ${LABEL_COLORS[label.color].bgClassName}`}
            />
            <span className="truncate">{label.name}</span>
          </button>
        );
      })}
    </div>
  );

  if (isDesktop) {
    return (
      <>
        <MenuSearchInput
          placeholder="Search labels"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        {labelsListComponent}
        <div className="flex gap-x-2 p-1.5">{buttonComponents}</div>
      </>
    );
  }
  return (
    <>
      <DrawerHeader title="Add labels" onBack={onBack} />
      <DrawerBody>
        <SearchInput
          variant="lg"
          placeholder="Search labels"
          value={searchText}
          onClear={() => setSearchText("")}
          onChange={(event) => setSearchText(event.target.value)}
        />
        {labelsListComponent}
      </DrawerBody>
      <DrawerFooter className="justify-end">{buttonComponents}</DrawerFooter>
    </>
  );
}
